import { useVirtualizer } from "@tanstack/react-virtual";
import { useMemo, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Button } from "@/components/ui/button";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "/pdf.worker.min.mjs",
  window.location.origin,
).toString();

const PREVIEW_PAGE_WIDTH = 152;
const ESTIMATED_PREVIEW_HEIGHT = 268;

type PdfPagePreviewSelectorProps = {
  file: File;
  pageCount: number | null;
  selectedPageIndices: number[];
  isBusy: boolean;
  isReadingPdf: boolean;
  pdfLoadErrorMessage: string | null;
  onChangeSelectedPageIndices: (pageIndices: number[]) => void;
};

function createAllPageIndices(pageCount: number) {
  return Array.from({ length: pageCount }, (_, pageIndex) => pageIndex);
}

function toSortedUniquePageIndices(pageIndices: number[]) {
  return [...new Set(pageIndices)].sort((left, right) => left - right);
}

function getSelectedSummary(selectedCount: number, totalCount: number) {
  return `${selectedCount} di ${totalCount} pagine selezionate. Solo queste verranno inviate in analisi.`;
}

export function PdfPagePreviewSelector({
  file,
  pageCount,
  selectedPageIndices,
  isBusy,
  isReadingPdf,
  pdfLoadErrorMessage,
  onChangeSelectedPageIndices,
}: PdfPagePreviewSelectorProps) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [previewErrorMessage, setPreviewErrorMessage] = useState<string | null>(
    null,
  );

  const totalPages = pageCount ?? 0;
  const selectedPagesCount = selectedPageIndices.length;
  const selectedPageLookup = useMemo(
    () => new Set(selectedPageIndices),
    [selectedPageIndices],
  );
  const rowVirtualizer = useVirtualizer({
    count: totalPages,
    getScrollElement: () => scrollElement,
    estimateSize: () => ESTIMATED_PREVIEW_HEIGHT,
    overscan: 4,
  });

  function updateSelectedPages(nextPageIndices: number[]) {
    onChangeSelectedPageIndices(toSortedUniquePageIndices(nextPageIndices));
  }

  function togglePage(pageIndex: number) {
    if (selectedPageLookup.has(pageIndex)) {
      updateSelectedPages(
        selectedPageIndices.filter(
          (selectedPageIndex) => selectedPageIndex !== pageIndex,
        ),
      );
      return;
    }

    updateSelectedPages([...selectedPageIndices, pageIndex]);
  }

  function selectAllPages() {
    if (!pageCount) return;
    updateSelectedPages(createAllPageIndices(pageCount));
  }

  function clearSelectedPages() {
    updateSelectedPages([]);
  }

  return (
    <div className="upload-page-selector">
      <div className="upload-page-selector__header">
        <div className="upload-page-selector__copy">
          <span className="upload-form__label">Pagine da analizzare</span>
          <p className="upload-page-selector__hint">
            {isReadingPdf
              ? "Lettura pagine del PDF in corso…"
              : pageCount
                ? getSelectedSummary(selectedPagesCount, pageCount)
                : "Preparazione selettore pagine…"}
          </p>
        </div>
        <div className="upload-page-selector__actions">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={selectAllPages}
            disabled={isBusy || pageCount === null}
          >
            Tutte
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clearSelectedPages}
            disabled={isBusy || pageCount === null}
          >
            Nessuna
          </Button>
        </div>
      </div>

      {pdfLoadErrorMessage ? (
        <p className="upload-form__error" role="alert">
          Errore: {pdfLoadErrorMessage}
        </p>
      ) : null}
      {pageCount !== null && selectedPagesCount === 0 ? (
        <p className="upload-form__error" role="alert">
          Seleziona almeno una pagina prima di inviare il PDF.
        </p>
      ) : null}
      {previewErrorMessage ? (
        <p className="upload-form__error" role="alert">
          Errore anteprima: {previewErrorMessage}
        </p>
      ) : null}

      {pageCount ? (
        <Document
          file={file}
          error={null}
          loading={
            <div className="upload-page-selector__loading">
              Caricamento anteprime PDF…
            </div>
          }
          onLoadError={(error) => {
            setPreviewErrorMessage(
              error.message || "Impossibile generare l'anteprima del PDF.",
            );
          }}
        >
          {() => (
            <div className="upload-page-selector__viewer">
              <div
                ref={setScrollElement}
                className="upload-page-selector__scroller"
              >
                <div
                  className="upload-page-selector__virtual-space"
                  style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                    const pageIndex = virtualItem.index;
                    const pageNumber = pageIndex + 1;
                    const isSelected = selectedPageLookup.has(pageIndex);

                    return (
                      <div
                        key={pageNumber}
                        data-index={pageIndex}
                        ref={rowVirtualizer.measureElement}
                        className="upload-page-selector__virtual-row"
                        style={{
                          transform: `translateY(${virtualItem.start}px)`,
                        }}
                      >
                        <label
                          className={`upload-page-selector__card ${isSelected ? "upload-page-selector__card--selected" : ""}`}
                        >
                          <input
                            className="upload-page-selector__checkbox"
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => togglePage(pageIndex)}
                            disabled={isBusy}
                          />
                          <div className="upload-page-selector__card-copy">
                            <span className="upload-page-selector__page-label">
                              Pagina {pageNumber}
                            </span>
                            <span className="upload-page-selector__page-meta">
                              {isSelected ? "Inclusa nell'analisi" : "Esclusa"}
                            </span>
                          </div>
                          <div className="upload-page-selector__thumbnail-frame">
                            <Page
                              devicePixelRatio={1}
                              error={
                                <div className="upload-page-selector__thumb-state">
                                  Anteprima non disponibile
                                </div>
                              }
                              loading={
                                <div className="upload-page-selector__thumb-state">
                                  Rendering pagina…
                                </div>
                              }
                              pageNumber={pageNumber}
                              renderAnnotationLayer={false}
                              renderTextLayer={false}
                              width={PREVIEW_PAGE_WIDTH}
                            />
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </Document>
      ) : null}
    </div>
  );
}
